// =============================================================================
// LP Express — "Coffee Shop Lead Capture (Gaffle)" template renderer.
//
// Renders a `generatedContent.coffeeShop` blob (see shared/lp-express-coffee-shop)
// as the imported Gaffle coffee-shop lead-capture page: scrolling ticker →
// header (logo) → hero + lead form → drinks gallery → reel video → them-vs-us
// callout → values → visit-the-shop pour bar → FAQ → bottom CTA + lead form →
// footer → sticky bottom bar.
//
//  • Copy is fully editable via the shared path-based `setText` ops (paths
//    like ["coffeeShop","hero","headline"]).
//  • Sections reorder/hide/delete through ONE page-level LpReorderGroup +
//    per-section EditableElement.
//  • Imagery: the export's asset binaries were not included, so photo
//    surfaces are AI slots (generatedContent.coffeeShopImages, per-slot
//    spinner/failed/retry) with the usual media-picker/upload override; the
//    logo slots fall back to the real brand logo; the reel video is a
//    media-picker-only slot (generatedContent.videoOverrides["coffee-shop-reel"]).
//  • Lead capture: the two lead forms are INERT in the builder (copy stays
//    editable) and LIVE only on the published public page, where
//    `ctx.publicLeadContext` carries {brandSlug, slug} and submissions POST to
//    /api/public/lp-express/:brandSlug/:slug/leads. The returned coupon code
//    renders in an on-screen success card. The export's Meta Pixel is
//    intentionally omitted.
//  • Per-section copy regen anchors on each section heading with dynamic block
//    keys `coffee-shop-<sectionKey>` (see regenerate-coffee-shop-section).
// =============================================================================
import { useState } from "react";
import {
  BadgeCheck, Check, CheckCircle2, Clock, ImageIcon, Loader2, Mail, MapPin,
  Minus, Phone, PlayCircle, RotateCw, Star, X,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  LpReorderGroup,
  LpImageActions,
  LinkUrlPopover,
  VideoUrlEditor,
  useLpEditor,
  useLpMediaOverride,
} from "@/components/lp-editor";
import {
  normalizeLpCoffeeShopContent,
  LP_COFFEE_SHOP_SECTIONS,
  LP_COFFEE_SHOP_IMAGE_SLOTS,
  type LpCoffeeShopContent,
} from "@shared/lp-express-coffee-shop";

// AI-generatable slot keys (hero-photo, drink-0..3, pourbar-0..2). Cards past
// the last defined slot fall back to the plain upload/media-picker placeholder.
const COFFEE_SHOP_AI_SLOT_KEYS = new Set(LP_COFFEE_SHOP_IMAGE_SLOTS.map((s) => s.key));

export type CoffeeShopCtx = {
  brand: any;
  generatedContent: any;
  logoUrl: string | null;
  typography?: { heading?: string; body?: string } | any;
  /** Retry a single failed AI image slot (coffeeShopImages). In-app builder
   *  only — the public/published page leaves it undefined so failed slots
   *  stay as static placeholders. */
  onRetrySlot?: (mergeKey: "coffeeShopImages", slotKey: string) => void;
  /** Present ONLY on the published public page — carries the route params the
   *  lead forms need to POST to the public lead-capture endpoint. When
   *  undefined (builder/preview) the forms render inert. */
  publicLeadContext?: { brandSlug: string; slug: string };
};

// ── Palette ───────────────────────────────────────────────────────────────
// Color roles resolve to the BRAND's palette via the same `--color-brand-*`
// CSS variables the cold-drip/v2 templates use (brandCssVarsV2 applies them at
// the renderer root). The var() fallbacks are the original Gaffle export hexes.
const RED = "var(--color-brand-coral, #AE1F2E)";
const TEAL = "var(--color-brand-navy, #0BB6D5)";
const WINE = "var(--color-brand-deepnavy, #600A2C)";
const CREAM = "var(--color-brand-cream, #FDF4F0)";
const INK = "var(--color-brand-text, #1D1D1F)";
const MUTED = "#6B6B6B";
const BORDER = "rgba(29,29,31,0.12)";

// "Oaxaca Prickly" is served via @font-face in index.css (licensed binary at
// client/public/fonts/OaxacaPrickly.otf); Georgia is the load-failure fallback.
const DISPLAY_FONT = '"Oaxaca Prickly", Georgia, "Times New Roman", serif';

const LEAD_FORM_ANCHOR = "coffee-shop-lead-form";

// ── Media slots ──────────────────────────────────────────────────────────────

function coffeeShopMediaKey(slot: string): string {
  return `coffee-shop:img:${slot}`;
}

function MediaSlot({
  slot,
  label,
  className,
  style,
  testId,
  fallbackUrl,
  aiSlot,
  onRegenerate,
}: {
  slot: string;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  testId: string;
  fallbackUrl?: string | null;
  aiSlot?: { imageUrl?: string | null; imageStatus?: string | null; altText?: string | null } | null;
  onRegenerate?: () => void;
}) {
  const overrideUrl = useLpMediaOverride(coffeeShopMediaKey(slot));
  const editor = useLpEditor();
  const actions = (
    <LpImageActions
      slotKey={coffeeShopMediaKey(slot)}
      canRegenerate={!!onRegenerate}
      onRegenerate={onRegenerate}
      testIdPrefix={testId}
    />
  );
  const aiUrl =
    aiSlot?.imageStatus === "ready" && aiSlot.imageUrl ? aiSlot.imageUrl : null;
  const url = overrideUrl || aiUrl || fallbackUrl || null;
  if (url) {
    return (
      <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
        <img
          src={url}
          alt={aiSlot?.altText || label || ""}
          loading="lazy"
          className={className}
          style={{ objectFit: "cover", display: "block", width: "100%", height: "100%", ...style }}
          data-testid={testId}
        />
        {actions}
      </div>
    );
  }
  return (
    <div className="group/lpimg relative" style={{ width: "100%", height: "100%" }}>
      <div
        className={className}
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 6, width: "100%", height: "100%", minHeight: 120, backgroundColor: CREAM,
          border: `1px dashed ${BORDER}`, borderRadius: 12, ...style,
        }}
        data-testid={testId}
      >
        {aiSlot?.imageStatus === "pending" ? (
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: MUTED }} />
        ) : aiSlot?.imageStatus === "failed" && editor && onRegenerate ? (
          <button
            type="button"
            onClick={onRegenerate}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
            style={{ color: MUTED, border: `1px solid ${BORDER}`, backgroundColor: "#fff" }}
            data-testid={`${testId}-retry`}
          >
            <RotateCw className="h-3 w-3" /> Retry image
          </button>
        ) : (
          <ImageIcon className="h-5 w-5" style={{ color: MUTED }} />
        )}
        {editor && aiSlot?.imageStatus !== "pending" && (
          <span style={{ fontSize: 11, color: MUTED, textAlign: "center", padding: "0 8px" }}>
            {aiSlot?.imageStatus === "failed"
              ? "Image generation failed"
              : label || "Upload or pick an image"}
          </span>
        )}
      </div>
      {actions}
    </div>
  );
}

// Section shell: EditableElement wrapper; regen anchoring is per-heading.
function Shell({
  k,
  children,
  style,
}: {
  k: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <EditableElement elementKey={`coffee-shop-${k}`} toolbarSide="left">
      <section style={style} data-testid={`coffee-shop-section-${k}`}>
        {children}
      </section>
    </EditableElement>
  );
}

const wrap: React.CSSProperties = { maxWidth: 1080, margin: "0 auto", padding: "0 20px" };

function Eyebrow({ value, path, testId, color = TEAL }: { value: string; path: (string | number)[]; testId: string; color?: string }) {
  return (
    <EditableText
      value={value}
      path={path}
      as="div"
      style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color, marginBottom: 10 }}
      placeholder="Eyebrow"
      testId={testId}
    />
  );
}

function Heading({
  value, path, testId, regenKey, color = INK, size = 34,
}: {
  value: string; path: (string | number)[]; testId: string; regenKey: string; color?: string; size?: number;
}) {
  return (
    <EditableText
      value={value}
      path={path}
      as="h2"
      style={{ fontFamily: DISPLAY_FONT, fontSize: size, lineHeight: 1.15, fontWeight: 800, color, margin: "0 0 14px" }}
      placeholder="Heading"
      testId={testId}
      regenBlockKey={regenKey}
      regenAnchor
    />
  );
}

// ── Lead form ────────────────────────────────────────────────────────────────
// Editable copy in the builder (form controls inert), live submission on the
// published page when publicLeadContext is provided. Success replaces the form
// with the coupon-reveal card.

function LeadForm({
  cs,
  P,
  rk,
  publicLeadContext,
  idSuffix,
  anchor,
}: {
  cs: LpCoffeeShopContent;
  P: (...p: (string | number)[]) => (string | number)[];
  rk: (key: string) => string;
  publicLeadContext?: { brandSlug: string; slug: string };
  idSuffix: string;
  anchor?: boolean;
}) {
  const editor = useLpEditor();
  const lf = cs.leadForm;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState<string | null>(null);

  const live = !!publicLeadContext && !editor;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!live || pending) return;
    setError(null);
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError(lf.errors.missingFields);
      return;
    }
    if (phone.replace(/\D/g, "").length < 7) {
      setError(lf.errors.shortPhone);
      return;
    }
    setPending(true);
    try {
      const res = await fetch(
        `/api/public/lp-express/${encodeURIComponent(publicLeadContext!.brandSlug)}/${encodeURIComponent(publicLeadContext!.slug)}/leads`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ firstName: name.trim(), email: email.trim(), phone: phone.trim() }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.couponCode) {
        setError(typeof data?.message === "string" && data.message ? data.message : lf.errors.generic);
        return;
      }
      setCouponCode(String(data.couponCode));
    } catch {
      setError(lf.errors.generic);
    } finally {
      setPending(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "12px 14px", fontSize: 15, borderRadius: 10,
    border: `1px solid ${BORDER}`, backgroundColor: "#fff", color: INK, outline: "none",
  };

  return (
    <div
      id={anchor ? LEAD_FORM_ANCHOR : undefined}
      style={{
        backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 18,
        padding: "26px 24px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", scrollMarginTop: 80,
      }}
      data-testid={`card-coffee-shop-lead-form-${idSuffix}`}
    >
      {couponCode ? (
        <div style={{ textAlign: "center", padding: "10px 0" }} data-testid={`card-coffee-shop-coupon-${idSuffix}`}>
          <CheckCircle2 className="h-9 w-9" style={{ color: TEAL, margin: "0 auto 10px" }} />
          <div style={{ fontFamily: DISPLAY_FONT, fontSize: 26, fontWeight: 900, color: INK, marginBottom: 8 }} data-testid={`text-coffee-shop-success-heading-${idSuffix}`}>
            {lf.success.heading}
          </div>
          <div
            style={{
              fontFamily: "ui-monospace, Menlo, monospace", fontSize: 28, fontWeight: 900, letterSpacing: "0.08em",
              color: WINE, backgroundColor: CREAM, border: `2px dashed ${WINE}`, borderRadius: 12,
              padding: "14px 10px", margin: "0 0 12px",
            }}
            data-testid={`text-coffee-shop-coupon-code-${idSuffix}`}
          >
            {couponCode}
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: MUTED, margin: 0 }} data-testid={`text-coffee-shop-success-body-${idSuffix}`}>
            {lf.success.body}
          </p>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <Eyebrow value={lf.eyebrow} path={P("leadForm", "eyebrow")} testId={`text-coffee-shop-form-eyebrow-${idSuffix}`} color={RED} />
          <EditableText
            value={lf.heading}
            path={P("leadForm", "heading")}
            as="h3"
            style={{ fontFamily: DISPLAY_FONT, fontSize: 26, fontWeight: 900, color: INK, margin: "0 0 16px" }}
            placeholder="Form heading"
            testId={`text-coffee-shop-form-heading-${idSuffix}`}
            regenBlockKey={rk("hero")}
          />
          {lf.fields.map((f, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12.5, fontWeight: 800, color: INK, marginBottom: 5 }}>
                <EditableText value={f.label} path={P("leadForm", "fields", i, "label")} as="span" placeholder="Label" testId={`text-coffee-shop-form-label-${i}-${idSuffix}`} regenBlockKey={rk("hero")} />
              </label>
              {editor ? (
                <div style={{ ...inputStyle, color: MUTED, cursor: "default" }} data-testid={`input-coffee-shop-${i}-${idSuffix}`}>
                  <EditableText value={f.placeholder} path={P("leadForm", "fields", i, "placeholder")} as="span" placeholder="Placeholder" testId={`text-coffee-shop-form-placeholder-${i}-${idSuffix}`} regenBlockKey={rk("hero")} />
                </div>
              ) : (
                <input
                  type={i === 1 ? "email" : i === 2 ? "tel" : "text"}
                  value={i === 0 ? name : i === 1 ? email : phone}
                  onChange={(e) => (i === 0 ? setName : i === 1 ? setEmail : setPhone)(e.target.value)}
                  placeholder={f.placeholder}
                  disabled={!live || pending}
                  style={inputStyle}
                  data-testid={`input-coffee-shop-${i === 0 ? "name" : i === 1 ? "email" : "phone"}-${idSuffix}`}
                />
              )}
            </div>
          ))}
          {error && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 13, color: "#B42318", marginBottom: 10 }} data-testid={`text-coffee-shop-form-error-${idSuffix}`}>
              <X className="h-4 w-4" style={{ flexShrink: 0, marginTop: 1 }} /> {error}
            </div>
          )}
          <button
            type="submit"
            disabled={!live || pending}
            style={{
              display: "block", width: "100%", padding: "15px 0", backgroundColor: RED, color: "#fff",
              fontSize: 15, fontWeight: 800, textAlign: "center", borderRadius: 999, border: "none",
              letterSpacing: "0.02em", boxShadow: "0 4px 14px rgba(0,0,0,0.16)",
              cursor: live ? "pointer" : "default", opacity: pending ? 0.7 : 1,
            }}
            data-testid={`button-coffee-shop-submit-${idSuffix}`}
          >
            {pending ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Loader2 className="h-4 w-4 animate-spin" /> {lf.submitPending}
              </span>
            ) : (
              <EditableText value={lf.submit} path={P("leadForm", "submit")} as="span" noHideDelete placeholder="Submit" testId={`text-coffee-shop-submit-label-${idSuffix}`} regenBlockKey={rk("hero")} />
            )}
          </button>
          <EditableText
            value={lf.footnote}
            path={P("leadForm", "footnote")}
            as="p"
            multiline
            style={{ fontSize: 12.5, lineHeight: 1.55, color: MUTED, margin: "12px 0 0", textAlign: "center" }}
            placeholder="Footnote"
            testId={`text-coffee-shop-form-footnote-${idSuffix}`}
            regenBlockKey={rk("hero")}
          />
        </form>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function LpExpressCoffeeShop({ ctx }: { ctx: CoffeeShopCtx }) {
  const cs: LpCoffeeShopContent = normalizeLpCoffeeShopContent(ctx.generatedContent?.coffeeShop);
  const editor = useLpEditor();

  const rk = (key: string) => `coffee-shop-${key}`;
  const P = (...p: (string | number)[]) => ["coffeeShop", ...p];

  // AI image slots (generatedContent.coffeeShopImages, keyed by slot key).
  const aiSlots: Record<string, { imageUrl?: string | null; imageStatus?: string | null; altText?: string | null }> = {};
  for (const s of (ctx.generatedContent?.coffeeShopImages ?? []) as any[]) {
    if (s?.key) aiSlots[s.key] = s;
  }
  const retrySlot = ctx.onRetrySlot
    ? (slotKey: string) =>
        COFFEE_SHOP_AI_SLOT_KEYS.has(slotKey)
          ? () => ctx.onRetrySlot!("coffeeShopImages", slotKey)
          : undefined
    : (_slotKey: string) => undefined;

  // Reel video: media-picker/URL/upload-only slot on videoOverrides.
  const reelUrl: string | undefined =
    (ctx.generatedContent?.videoOverrides as any)?.["coffee-shop-reel"] || undefined;

  const scrollToForm = (e: React.MouseEvent) => {
    e.preventDefault();
    if (editor) return;
    const doc = (e.currentTarget as HTMLElement).ownerDocument;
    doc.getElementById(LEAD_FORM_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const renderers: Record<string, () => React.ReactNode> = {
    ticker: () => (
      <Shell k="ticker" style={{ backgroundColor: WINE, overflow: "hidden" }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 0", padding: "9px 12px" }}>
          {cs.ticker.map((t, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
              <EditableText
                value={t}
                path={P("ticker", i)}
                as="span"
                style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#fff", whiteSpace: "nowrap" }}
                placeholder="Ticker item"
                testId={`text-coffee-shop-ticker-${i}`}
                regenBlockKey={rk("ticker")}
                regenAnchor={i === 0}
              />
              {i < cs.ticker.length - 1 && <span style={{ color: "rgba(255,255,255,0.45)", margin: "0 14px", fontSize: 11 }}>•</span>}
            </span>
          ))}
        </div>
      </Shell>
    ),
    header: () => (
      <Shell k="header" style={{ backgroundColor: CREAM, borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ ...wrap, display: "flex", justifyContent: "center", padding: "14px 20px" }}>
          <div style={{ width: 140, height: 44 }}>
            <MediaSlot slot="header-logo" label="Logo" testId="img-coffee-shop-header-logo" fallbackUrl={ctx.logoUrl} style={{ objectFit: "contain", minHeight: 44, borderRadius: 6 }} />
          </div>
        </div>
      </Shell>
    ),
    hero: () => (
      <Shell k="hero" style={{ backgroundColor: CREAM, padding: "48px 0 60px" }}>
        <div style={{ ...wrap }} className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-start">
          <div>
            <Eyebrow value={cs.hero.eyebrow} path={P("hero", "eyebrow")} testId="text-coffee-shop-hero-eyebrow" color={RED} />
            <EditableText
              value={cs.hero.headline}
              path={P("hero", "headline")}
              as="h1"
              style={{ fontFamily: DISPLAY_FONT, fontSize: 44, lineHeight: 1.08, fontWeight: 900, color: INK, margin: "0 0 16px" }}
              placeholder="Headline"
              testId="text-coffee-shop-hero-headline"
              regenBlockKey={rk("hero")}
              regenAnchor
            />
            <EditableText
              value={cs.hero.body}
              path={P("hero", "body")}
              as="p"
              multiline
              style={{ fontSize: 16, lineHeight: 1.65, color: MUTED, margin: "0 0 18px", maxWidth: 520 }}
              placeholder="Body"
              testId="text-coffee-shop-hero-body"
              regenBlockKey={rk("hero")}
            />
            <div style={{ marginBottom: 18 }}>
              {cs.hero.bullets.map((b, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                  <Check className="h-4 w-4" style={{ color: TEAL, flexShrink: 0, marginTop: 3 }} />
                  <EditableText value={b} path={P("hero", "bullets", i)} as="span" style={{ fontSize: 14.5, lineHeight: 1.5, color: INK }} placeholder="Bullet" testId={`text-coffee-shop-hero-bullet-${i}`} regenBlockKey={rk("hero")} />
                </div>
              ))}
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
              <span style={{ display: "inline-flex", gap: 2 }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4" style={{ color: "#E8A33D", fill: "#E8A33D" }} />
                ))}
              </span>
              <EditableText value={cs.hero.socialProof} path={P("hero", "socialProof")} as="span" style={{ fontSize: 13, fontWeight: 700, color: MUTED }} placeholder="Social proof" testId="text-coffee-shop-hero-social" regenBlockKey={rk("hero")} />
            </div>
            <div style={{ position: "relative", borderRadius: 18, overflow: "hidden", aspectRatio: "4 / 3", maxWidth: 520 }}>
              <MediaSlot slot="hero-photo" label={cs.hero.imageAlt} testId="img-coffee-shop-hero" aiSlot={aiSlots["hero-photo"]} onRegenerate={retrySlot("hero-photo")} style={{ borderRadius: 18 }} />
              <div style={{ position: "absolute", left: 12, bottom: 12, backgroundColor: "rgba(29,29,31,0.78)", borderRadius: 10, padding: "8px 12px", pointerEvents: "none" }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "#fff" }} data-testid="text-coffee-shop-hero-caption">{cs.hero.imageCaption}</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "rgba(255,255,255,0.75)" }}>
                  <MapPin className="h-3 w-3" /> {cs.hero.imageLocation}
                </div>
              </div>
            </div>
            {editor && (
              <div style={{ display: "flex", gap: 14, marginTop: 8, maxWidth: 520 }}>
                <EditableText value={cs.hero.imageCaption} path={P("hero", "imageCaption")} as="span" style={{ fontSize: 12, color: MUTED }} placeholder="Image caption" testId="text-coffee-shop-hero-caption-edit" regenBlockKey={rk("hero")} />
                <EditableText value={cs.hero.imageLocation} path={P("hero", "imageLocation")} as="span" style={{ fontSize: 12, color: MUTED }} placeholder="Image location" testId="text-coffee-shop-hero-location-edit" regenBlockKey={rk("hero")} />
              </div>
            )}
          </div>
          <div className="lg:sticky lg:top-6">
            <LeadForm cs={cs} P={P} rk={rk} publicLeadContext={ctx.publicLeadContext} idSuffix="hero" anchor />
          </div>
        </div>
      </Shell>
    ),
    drinks: () => (
      <Shell k="drinks" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <Eyebrow value={cs.drinks.eyebrow} path={P("drinks", "eyebrow")} testId="text-coffee-shop-drinks-eyebrow" />
            <Heading value={cs.drinks.heading} path={P("drinks", "heading")} testId="text-coffee-shop-drinks-heading" regenKey={rk("drinks")} />
            <EditableText value={cs.drinks.intro} path={P("drinks", "intro")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.6, color: MUTED, margin: "0 auto", maxWidth: 560 }} placeholder="Intro" testId="text-coffee-shop-drinks-intro" regenBlockKey={rk("drinks")} />
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {cs.drinks.items.slice(0, 4).map((d, i) => (
              <div key={i} style={{ border: `1px solid ${BORDER}`, borderRadius: 16, overflow: "hidden", backgroundColor: CREAM }} data-testid={`card-coffee-shop-drink-${i}`}>
                <div style={{ aspectRatio: "1 / 1" }}>
                  <MediaSlot slot={`drink-${i}`} label={d.name} testId={`img-coffee-shop-drink-${i}`} aiSlot={aiSlots[`drink-${i}`]} onRegenerate={retrySlot(`drink-${i}`)} style={{ borderRadius: 0 }} />
                </div>
                <div style={{ padding: "14px 16px 18px" }}>
                  <EditableText value={d.tag} path={P("drinks", "items", i, "tag")} as="div" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: RED, marginBottom: 6 }} placeholder="Tag" testId={`text-coffee-shop-drink-tag-${i}`} regenBlockKey={rk("drinks")} />
                  <EditableText value={d.name} path={P("drinks", "items", i, "name")} as="h3" style={{ fontFamily: DISPLAY_FONT, fontSize: 19, fontWeight: 900, color: INK, margin: "0 0 6px" }} placeholder="Name" testId={`text-coffee-shop-drink-name-${i}`} regenBlockKey={rk("drinks")} />
                  <EditableText value={d.line} path={P("drinks", "items", i, "line")} as="p" multiline style={{ fontSize: 13.5, lineHeight: 1.55, color: MUTED, margin: 0 }} placeholder="Description" testId={`text-coffee-shop-drink-line-${i}`} regenBlockKey={rk("drinks")} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Shell>
    ),
    reel: () => (
      <Shell k="reel" style={{ backgroundColor: CREAM, padding: "60px 0" }}>
        <div style={{ ...wrap, maxWidth: 720, textAlign: "center" }}>
          <Eyebrow value={cs.reel.eyebrow} path={P("reel", "eyebrow")} testId="text-coffee-shop-reel-eyebrow" />
          <Heading value={cs.reel.heading} path={P("reel", "heading")} testId="text-coffee-shop-reel-heading" regenKey={rk("reel")} />
          <EditableText value={cs.reel.body} path={P("reel", "body")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.6, color: MUTED, margin: "0 auto 22px", maxWidth: 520 }} placeholder="Body" testId="text-coffee-shop-reel-body" regenBlockKey={rk("reel")} />
          <div style={{ position: "relative", borderRadius: 18, overflow: "hidden", backgroundColor: "#111", aspectRatio: "9 / 16", maxWidth: 340, margin: "0 auto" }}>
            {reelUrl ? (
              <video
                src={reelUrl}
                controls
                playsInline
                preload="metadata"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                data-testid="video-coffee-shop-reel"
              />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", height: "100%", color: "rgba(255,255,255,0.6)" }} data-testid="video-coffee-shop-reel-placeholder">
                <PlayCircle className="h-9 w-9" />
                {editor && <span style={{ fontSize: 12 }}>Add a reel video</span>}
              </div>
            )}
            {editor && <VideoUrlEditor slot="coffee-shop-reel" currentOverride={reelUrl} />}
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 12, fontSize: 12.5, color: MUTED }}>
            <MapPin className="h-3.5 w-3.5" />
            <EditableText value={cs.reel.caption} path={P("reel", "caption")} as="span" placeholder="Caption" testId="text-coffee-shop-reel-caption" regenBlockKey={rk("reel")} />
          </div>
        </div>
      </Shell>
    ),
    industryCallout: () => (
      <Shell k="industryCallout" style={{ backgroundColor: WINE, padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <Eyebrow value={cs.industryCallout.eyebrow} path={P("industryCallout", "eyebrow")} testId="text-coffee-shop-industry-eyebrow" color="rgba(255,255,255,0.75)" />
            <Heading value={cs.industryCallout.heading} path={P("industryCallout", "heading")} testId="text-coffee-shop-industry-heading" regenKey={rk("industryCallout")} color="#fff" />
            <EditableText value={cs.industryCallout.intro} path={P("industryCallout", "intro")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.65, color: "rgba(255,255,255,0.85)", margin: "0 auto", maxWidth: 640 }} placeholder="Intro" testId="text-coffee-shop-industry-intro" regenBlockKey={rk("industryCallout")} />
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2" style={{ maxWidth: 860, margin: "0 auto" }}>
            <div style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "22px 22px" }} data-testid="card-coffee-shop-them">
              <EditableText value={cs.industryCallout.them.label} path={P("industryCallout", "them", "label")} as="div" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginBottom: 14 }} placeholder="Label" testId="text-coffee-shop-them-label" regenBlockKey={rk("industryCallout")} />
              {cs.industryCallout.them.items.map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                  <X className="h-4 w-4" style={{ color: "rgba(255,255,255,0.5)", flexShrink: 0, marginTop: 3 }} />
                  <EditableText value={t} path={P("industryCallout", "them", "items", i)} as="span" style={{ fontSize: 14, lineHeight: 1.55, color: "rgba(255,255,255,0.75)" }} placeholder="Item" testId={`text-coffee-shop-them-${i}`} regenBlockKey={rk("industryCallout")} />
                </div>
              ))}
            </div>
            <div style={{ backgroundColor: "#fff", borderRadius: 16, padding: "22px 22px" }} data-testid="card-coffee-shop-us">
              <EditableText value={cs.industryCallout.us.label} path={P("industryCallout", "us", "label")} as="div" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: RED, marginBottom: 14 }} placeholder="Label" testId="text-coffee-shop-us-label" regenBlockKey={rk("industryCallout")} />
              {cs.industryCallout.us.items.map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                  <BadgeCheck className="h-4 w-4" style={{ color: TEAL, flexShrink: 0, marginTop: 3 }} />
                  <EditableText value={t} path={P("industryCallout", "us", "items", i)} as="span" style={{ fontSize: 14, lineHeight: 1.55, color: INK }} placeholder="Item" testId={`text-coffee-shop-us-${i}`} regenBlockKey={rk("industryCallout")} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Shell>
    ),
    values: () => (
      <Shell k="values" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <Eyebrow value={cs.values.eyebrow} path={P("values", "eyebrow")} testId="text-coffee-shop-values-eyebrow" />
            <Heading value={cs.values.heading} path={P("values", "heading")} testId="text-coffee-shop-values-heading" regenKey={rk("values")} />
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {cs.values.items.map((v, i) => (
              <div key={i} style={{ backgroundColor: CREAM, border: `1px solid ${BORDER}`, borderRadius: 16, padding: "24px 22px" }} data-testid={`card-coffee-shop-value-${i}`}>
                <EditableText value={v.title} path={P("values", "items", i, "title")} as="h3" style={{ fontFamily: DISPLAY_FONT, fontSize: 20, fontWeight: 900, color: INK, margin: "0 0 8px" }} placeholder="Title" testId={`text-coffee-shop-value-title-${i}`} regenBlockKey={rk("values")} />
                <EditableText value={v.body} path={P("values", "items", i, "body")} as="p" multiline style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, margin: 0 }} placeholder="Body" testId={`text-coffee-shop-value-body-${i}`} regenBlockKey={rk("values")} />
              </div>
            ))}
          </div>
        </div>
      </Shell>
    ),
    pourBar: () => (
      <Shell k="pourBar" style={{ backgroundColor: CREAM, padding: "60px 0" }}>
        <div style={wrap}>
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 999, padding: "6px 14px", marginBottom: 12 }}>
              <MapPin className="h-3.5 w-3.5" style={{ color: RED }} />
              <EditableText value={cs.pourBar.badge} path={P("pourBar", "badge")} as="span" style={{ fontSize: 12, fontWeight: 800, color: INK }} placeholder="Badge" testId="text-coffee-shop-pourbar-badge" regenBlockKey={rk("pourBar")} />
            </div>
            <Heading value={cs.pourBar.heading} path={P("pourBar", "heading")} testId="text-coffee-shop-pourbar-heading" regenKey={rk("pourBar")} />
            <EditableText value={cs.pourBar.intro} path={P("pourBar", "intro")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.6, color: MUTED, margin: "0 auto", maxWidth: 560 }} placeholder="Intro" testId="text-coffee-shop-pourbar-intro" regenBlockKey={rk("pourBar")} />
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3" style={{ marginBottom: 28 }}>
            {cs.pourBar.slides.slice(0, 3).map((s, i) => (
              <figure key={i} style={{ margin: 0 }} data-testid={`card-coffee-shop-slide-${i}`}>
                <div style={{ aspectRatio: "4 / 3", borderRadius: 14, overflow: "hidden" }}>
                  <MediaSlot slot={`pourbar-${i}`} label={s.alt} testId={`img-coffee-shop-slide-${i}`} aiSlot={aiSlots[`pourbar-${i}`]} onRegenerate={retrySlot(`pourbar-${i}`)} style={{ borderRadius: 14 }} />
                </div>
                {(editor || s.caption) && (
                  <figcaption style={{ marginTop: 8, textAlign: "center" }}>
                    <EditableText value={s.caption} path={P("pourBar", "slides", i, "caption")} as="span" style={{ fontSize: 12.5, color: MUTED }} placeholder="Caption" testId={`text-coffee-shop-slide-caption-${i}`} regenBlockKey={rk("pourBar")} />
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4" style={{ maxWidth: 620, margin: "0 auto 32px", textAlign: "center", display: editor || cs.pourBar.stats.some((st) => st.num || st.label) ? undefined : "none" }}>
            {cs.pourBar.stats.map((st, i) => (
              <div key={i} style={{ visibility: editor || st.num || st.label ? undefined : "hidden" }} data-testid={`stat-coffee-shop-${i}`}>
                <EditableText value={st.num} path={P("pourBar", "stats", i, "num")} as="div" style={{ fontFamily: DISPLAY_FONT, fontSize: 28, fontWeight: 900, color: RED }} placeholder="Stat" testId={`text-coffee-shop-stat-num-${i}`} regenBlockKey={rk("pourBar")} />
                <EditableText value={st.label} path={P("pourBar", "stats", i, "label")} as="div" style={{ fontSize: 12.5, color: MUTED }} placeholder="Label" testId={`text-coffee-shop-stat-label-${i}`} regenBlockKey={rk("pourBar")} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2" style={{ maxWidth: 860, margin: "0 auto" }}>
            <div style={{ backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: "22px 22px" }} data-testid="card-coffee-shop-door-sign">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <EditableText value={cs.pourBar.doorSign.header} path={P("pourBar", "doorSign", "header")} as="h3" style={{ fontFamily: DISPLAY_FONT, fontSize: 20, fontWeight: 900, color: INK, margin: 0 }} placeholder="Door sign header" testId="text-coffee-shop-door-header" regenBlockKey={rk("pourBar")} />
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, backgroundColor: "rgba(11,182,213,0.12)", borderRadius: 999, padding: "4px 10px" }}>
                  <span style={{ width: 7, height: 7, borderRadius: 99, backgroundColor: TEAL }} />
                  <EditableText value={cs.pourBar.doorSign.openBanner} path={P("pourBar", "doorSign", "openBanner")} as="span" style={{ fontSize: 11.5, fontWeight: 800, color: INK }} placeholder="Open banner" testId="text-coffee-shop-door-open" regenBlockKey={rk("pourBar")} />
                </span>
              </div>
              {(editor || cs.pourBar.doorSign.hours.some((h) => h.hours)) && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 800, color: MUTED, marginBottom: 8 }}>
                  <Clock className="h-3.5 w-3.5" />
                  <EditableText value={cs.pourBar.doorSign.hoursLabel} path={P("pourBar", "doorSign", "hoursLabel")} as="span" placeholder="Hours label" testId="text-coffee-shop-door-hours-label" regenBlockKey={rk("pourBar")} />
                </div>
              )}
              {cs.pourBar.doorSign.hours.map((h, i) =>
                editor || h.hours ? (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, padding: "3px 0", color: INK }} data-testid={`row-coffee-shop-hours-${i}`}>
                    <EditableText value={h.day} path={P("pourBar", "doorSign", "hours", i, "day")} as="span" placeholder="Day" testId={`text-coffee-shop-hours-day-${i}`} regenBlockKey={rk("pourBar")} />
                    <EditableText value={h.hours} path={P("pourBar", "doorSign", "hours", i, "hours")} as="span" style={{ fontWeight: 700 }} placeholder="Hours" testId={`text-coffee-shop-hours-val-${i}`} regenBlockKey={rk("pourBar")} />
                  </div>
                ) : null,
              )}
              <div style={{ borderTop: `1px solid ${BORDER}`, marginTop: 12, paddingTop: 12 }}>
                <EditableText value={cs.pourBar.doorSign.addressCallout} path={P("pourBar", "doorSign", "addressCallout")} as="p" style={{ fontSize: 13, fontWeight: 800, color: WINE, margin: "0 0 6px" }} placeholder="Address callout" testId="text-coffee-shop-door-callout" regenBlockKey={rk("pourBar")} />
                {(editor || cs.pourBar.doorSign.address) && (
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                    <MapPin className="h-4 w-4" style={{ color: RED, flexShrink: 0, marginTop: 2 }} />
                    <EditableText value={cs.pourBar.doorSign.address} path={P("pourBar", "doorSign", "address")} as="span" style={{ fontSize: 13.5, color: INK }} placeholder="Address" testId="text-coffee-shop-door-address" regenBlockKey={rk("pourBar")} />
                  </div>
                )}
                {editor ? (
                  <span className="group/lplink relative inline-flex">
                    <span style={{ fontSize: 13, fontWeight: 800, color: TEAL, textDecoration: "underline" }}>
                      <EditableText value={cs.pourBar.doorSign.mapsCta} path={P("pourBar", "doorSign", "mapsCta")} as="span" placeholder="Maps" testId="text-coffee-shop-door-maps" regenBlockKey={rk("pourBar")} />
                    </span>
                    {editor.onEditLinkUrl && <LinkUrlPopover path={P("pourBar", "doorSign", "mapsUrl")} url={cs.pourBar.doorSign.mapsUrl} testId="link-coffee-shop-door-maps" />}
                  </span>
                ) : cs.pourBar.doorSign.mapsUrl && cs.pourBar.doorSign.mapsCta ? (
                  <a href={cs.pourBar.doorSign.mapsUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 800, color: TEAL, textDecoration: "underline" }} data-testid="link-coffee-shop-door-maps">
                    {cs.pourBar.doorSign.mapsCta}
                  </a>
                ) : null}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: "20px 22px" }} data-testid="card-coffee-shop-phone">
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <Phone className="h-4 w-4" style={{ color: RED }} />
                  <EditableText value={cs.pourBar.contacts.phoneLabel} path={P("pourBar", "contacts", "phoneLabel")} as="span" style={{ fontSize: 12.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: MUTED }} placeholder="Phone label" testId="text-coffee-shop-phone-label" regenBlockKey={rk("pourBar")} />
                </div>
                {editor ? (
                  <span className="group/lplink relative inline-flex">
                    <span style={{ fontSize: 17, fontWeight: 900, color: INK }}>
                      <EditableText value={cs.pourBar.contacts.phone} path={P("pourBar", "contacts", "phone")} as="span" placeholder="Phone" testId="text-coffee-shop-phone" regenBlockKey={rk("pourBar")} />
                    </span>
                    {editor.onEditLinkUrl && <LinkUrlPopover path={P("pourBar", "contacts", "phoneHref")} url={cs.pourBar.contacts.phoneHref} testId="link-coffee-shop-phone" />}
                  </span>
                ) : cs.pourBar.contacts.phone ? (
                  <a href={cs.pourBar.contacts.phoneHref || undefined} style={{ fontSize: 17, fontWeight: 900, color: INK, textDecoration: "none" }} data-testid="link-coffee-shop-phone">
                    {cs.pourBar.contacts.phone}
                  </a>
                ) : null}
              </div>
              <div style={{ backgroundColor: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: "20px 22px" }} data-testid="card-coffee-shop-email">
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <Mail className="h-4 w-4" style={{ color: RED }} />
                  <EditableText value={cs.pourBar.contacts.emailLabel} path={P("pourBar", "contacts", "emailLabel")} as="span" style={{ fontSize: 12.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: MUTED }} placeholder="Email label" testId="text-coffee-shop-email-label" regenBlockKey={rk("pourBar")} />
                </div>
                {editor ? (
                  <span className="group/lplink relative inline-flex">
                    <span style={{ fontSize: 15.5, fontWeight: 800, color: INK }}>
                      <EditableText value={cs.pourBar.contacts.email} path={P("pourBar", "contacts", "email")} as="span" placeholder="Email" testId="text-coffee-shop-email" regenBlockKey={rk("pourBar")} />
                    </span>
                    {editor.onEditLinkUrl && <LinkUrlPopover path={P("pourBar", "contacts", "emailHref")} url={cs.pourBar.contacts.emailHref} testId="link-coffee-shop-email" />}
                  </span>
                ) : cs.pourBar.contacts.email ? (
                  <a href={cs.pourBar.contacts.emailHref || undefined} style={{ fontSize: 15.5, fontWeight: 800, color: INK, textDecoration: "none" }} data-testid="link-coffee-shop-email">
                    {cs.pourBar.contacts.email}
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Shell>
    ),
    faq: () => (
      <Shell k="faq" style={{ backgroundColor: "#fff", padding: "60px 0" }}>
        <div style={{ ...wrap, maxWidth: 760 }}>
          <div style={{ textAlign: "center", marginBottom: 26 }}>
            <Eyebrow value={cs.faq.eyebrow} path={P("faq", "eyebrow")} testId="text-coffee-shop-faq-eyebrow" />
            <Heading value={cs.faq.heading} path={P("faq", "heading")} testId="text-coffee-shop-faq-heading" regenKey={rk("faq")} size={32} />
          </div>
          {cs.faq.items.map((f, i) => (
            <div key={i} style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: "16px 18px", marginBottom: 12 }} data-testid={`card-coffee-shop-faq-${i}`}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                <Minus className="h-4 w-4" style={{ color: RED, flexShrink: 0, marginTop: 3 }} />
                <EditableText value={f.q} path={P("faq", "items", i, "q")} as="h3" style={{ fontSize: 15.5, fontWeight: 800, color: INK, margin: 0 }} placeholder="Question" testId={`text-coffee-shop-faq-q-${i}`} regenBlockKey={rk("faq")} />
              </div>
              <EditableText value={f.a} path={P("faq", "items", i, "a")} as="p" multiline style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, margin: 0, paddingLeft: 26 }} placeholder="Answer" testId={`text-coffee-shop-faq-a-${i}`} regenBlockKey={rk("faq")} />
            </div>
          ))}
        </div>
      </Shell>
    ),
    bottomCta: () => (
      <Shell k="bottomCta" style={{ backgroundColor: RED, padding: "60px 0" }}>
        <div style={{ ...wrap, maxWidth: 640 }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <Eyebrow value={cs.bottomCta.eyebrow} path={P("bottomCta", "eyebrow")} testId="text-coffee-shop-bottom-eyebrow" color="rgba(255,255,255,0.8)" />
            <Heading value={cs.bottomCta.heading} path={P("bottomCta", "heading")} testId="text-coffee-shop-bottom-heading" regenKey={rk("bottomCta")} color="#fff" />
            <EditableText value={cs.bottomCta.body} path={P("bottomCta", "body")} as="p" multiline style={{ fontSize: 15, lineHeight: 1.6, color: "rgba(255,255,255,0.9)", margin: 0 }} placeholder="Body" testId="text-coffee-shop-bottom-body" regenBlockKey={rk("bottomCta")} />
          </div>
          <LeadForm cs={cs} P={P} rk={rk} publicLeadContext={ctx.publicLeadContext} idSuffix="bottom" />
        </div>
      </Shell>
    ),
    footer: () => (
      <Shell k="footer" style={{ backgroundColor: INK, padding: "44px 0 90px" }}>
        <div style={{ ...wrap, textAlign: "center" }}>
          <div style={{ width: 120, height: 40, margin: "0 auto 14px" }}>
            <MediaSlot slot="footer-logo" label="Footer logo" testId="img-coffee-shop-footer-logo" fallbackUrl={ctx.logoUrl} style={{ objectFit: "contain", minHeight: 40, borderRadius: 6 }} />
          </div>
          <EditableText value={cs.footer.tagline} path={P("footer", "tagline")} as="p" style={{ fontSize: 13.5, color: "rgba(255,255,255,0.7)", margin: "0 auto 16px", maxWidth: 480 }} placeholder="Tagline" testId="text-coffee-shop-footer-tagline" regenBlockKey={rk("footer")} regenAnchor />
          <div style={{ display: "flex", justifyContent: "center", gap: 22, marginBottom: 16 }}>
            {cs.footer.links.map((l, i) =>
              editor ? (
                <span key={i} className="group/lplink relative inline-flex">
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", textDecoration: "underline" }}>
                    <EditableText value={l.label} path={P("footer", "links", i, "label")} as="span" placeholder="Link" testId={`text-coffee-shop-footer-link-${i}`} regenBlockKey={rk("footer")} />
                  </span>
                  {editor.onEditLinkUrl && <LinkUrlPopover path={P("footer", "links", i, "href")} url={l.href} testId={`link-coffee-shop-footer-${i}`} />}
                </span>
              ) : (
                <a key={i} href={l.href || undefined} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 700, color: "#fff", textDecoration: "underline" }} data-testid={`link-coffee-shop-footer-${i}`}>
                  {l.label}
                </a>
              ),
            )}
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 6 }} data-testid="text-coffee-shop-footer-copyright">
            {cs.footer.copyright.replace("{year}", String(new Date().getFullYear()))}
          </div>
          <EditableText value={cs.footer.signoff} path={P("footer", "signoff")} as="div" style={{ fontFamily: DISPLAY_FONT, fontSize: 15, fontWeight: 800, color: TEAL }} placeholder="Signoff" testId="text-coffee-shop-footer-signoff" regenBlockKey={rk("footer")} />
        </div>
      </Shell>
    ),
    stickyBar: () => (
      // Editor/preview: render inline (as the page's last section) so the bar
      // stays inside the preview canvas instead of position:fixed escaping to
      // the app viewport and covering the builder chrome (same pattern as the
      // product-hero sticky bar). Public page: pin it to the bottom.
      <Shell k="stickyBar" style={editor
        ? { backgroundColor: "#fff", borderTop: `1px solid ${BORDER}`, boxShadow: "0 -6px 18px rgba(0,0,0,0.08)" }
        : { position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40, backgroundColor: "#fff", borderTop: `1px solid ${BORDER}`, boxShadow: "0 -6px 18px rgba(0,0,0,0.08)" }}>
        <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 20px" }}>
          <span style={{ fontSize: 13.5, fontWeight: 800, color: INK }}>
            <EditableText value={cs.stickyBar.label} path={P("stickyBar", "label")} as="span" style={{ fontWeight: 700, color: WINE }} placeholder="Sticky label" testId="text-coffee-shop-sticky-label" regenBlockKey={rk("stickyBar")} regenAnchor />
          </span>
          <a
            href={`#${LEAD_FORM_ANCHOR}`}
            onClick={scrollToForm}
            style={{
              display: "inline-block", padding: "12px 24px", backgroundColor: RED, color: "#fff",
              fontSize: 14, fontWeight: 800, borderRadius: 999, textDecoration: "none",
              boxShadow: "0 4px 14px rgba(0,0,0,0.16)", cursor: "pointer", whiteSpace: "nowrap",
            }}
            data-testid="button-coffee-shop-sticky-cta"
          >
            <EditableText value={cs.stickyBar.cta} path={P("stickyBar", "cta")} as="span" noHideDelete placeholder="Get 50% Off" testId="text-coffee-shop-sticky-cta-label" regenBlockKey={rk("stickyBar")} />
          </a>
        </div>
      </Shell>
    ),
  };

  return (
    <div style={{ backgroundColor: CREAM, minHeight: "100vh", fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif' }} data-testid="coffee-shop-page">
      <LpReorderGroup
        groupKey="coffeeShop:sections"
        items={LP_COFFEE_SHOP_SECTIONS.map((s) => ({
          key: `coffee-shop-${s.key}`,
          node: renderers[s.key] ? <div key={`coffee-shop-${s.key}`}>{renderers[s.key]()}</div> : null,
        })).filter((it) => it.node)}
      />
    </div>
  );
}
